import { z } from 'zod';
import { EffectSchema, OutcomeSchema, VisualRecipeSchema } from '../types.js';
import type { Outcome } from '../types.js';
import type { TemplateContext } from '../outcomes/templates.js';
import { buildTemplateOutcome } from '../outcomes/templates.js';

/** Feature-flagged LLM fallback. Disabled by default; server-only when enabled. */
export interface LlmOutcomeConfig {
  enabled: boolean;
  apiKey?: string;
  model?: string;
}

const LlmOutcomePayloadSchema = z.object({
  bridgeText: z.string().max(600),
  narratorQuip: z.string().max(200),
  effects: z.array(EffectSchema).max(8),
  callback: z.string().max(200).optional(),
  visual: VisualRecipeSchema,
});

export class LlmOutcomeClient {
  constructor(private config: LlmOutcomeConfig) {}

  isEnabled(): boolean {
    return Boolean(this.config.enabled && this.config.apiKey);
  }

  /** Stub: validates schema; in production would call OpenAI structured output. */
  tryResolve(key: string, ctx: TemplateContext): Outcome | null {
    if (!this.isEnabled()) return null;
    const template = buildTemplateOutcome(ctx);
    const payload = LlmOutcomePayloadSchema.safeParse({
      bridgeText: template.bridgeText,
      narratorQuip: template.narratorQuip,
      effects: template.effects,
      callback: template.callback,
      visual: template.visual,
    });
    if (!payload.success) return null;
    const outcome = OutcomeSchema.parse({
      key,
      tier: 'fallback',
      ...payload.data,
    });
    return outcome;
  }
}

export function createLlmClientFromEnv(env: Record<string, string | undefined>): LlmOutcomeClient {
  return new LlmOutcomeClient({
    enabled: env.PLOTJACK_LLM_FALLBACK === 'true',
    apiKey: env.OPENAI_API_KEY,
    model: env.PLOTJACK_LLM_MODEL ?? 'gpt-4o-mini',
  });
}
